import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    positionKey,
    parsePositionKey,
    isInside
} from '../../core/GridGeometry.js';
import { FloodGame } from './FloodGame.js';
import { Rational, CellTag, Basin, SliceState, rationalToNumber } from './FloodDebug.js';
import {
    reduceRational,
    addRationals,
    subtractRationals,
    multiplyRationals,
    divideRational,
    compareRationals
} from './FloodUtils.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

// Encode a parent as "sliceId:cellKey" (or null for faucet)
function encodeParent(sliceId: string, cellKey: string): string {
    return `${sliceId}:${cellKey}`;
}

function decodeParent(encoded: string): { sliceId: string; cellKey: string } {
    const colonIdx = encoded.lastIndexOf(':');
    return {
        sliceId: encoded.slice(0, colonIdx),
        cellKey: encoded.slice(colonIdx + 1)
    };
}

// Build a slice ID from an ancestry array
function ancestryToSliceId(ancestry: string[]): string {
    return ancestry.join('|');
}

export class FloodVortex extends FloodGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        // Recompute the flow simulation: a Hub gate deletion can prune an active
        // pair here, and checkWin/render read the cached fill times.
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.recalculate();
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, pairedPos) => {
            if (this.getGrid().hasDigitAt(p)) return false;
            if (this.getGrid().hasDigitAt(pairedPos)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.recalculate();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Vortex Flow Algorithm ─────────────────────────────────────

    private hasActiveGates(): boolean {
        return this.gm !== undefined && this.gm.hasActiveGates();
    }

    // Check if cell A has a passable gate to Z (gate exists and A is not in slice's ancestry)
    private getPassableGate(cellKey: string, ancestry: string[]): string | null {
        const pairedKey = this.gm.raw.get(cellKey);
        if (!pairedKey) return null;
        // Gate cannot be traversed twice in the same direction (A must not be in ancestry)
        if (ancestry.includes(cellKey)) return null;
        return pairedKey;
    }

    // Get or create a slice for the given ancestry
    private getOrCreateSlice(slices: Map<string, SliceState>, ancestry: string[]): SliceState {
        const id = ancestryToSliceId(ancestry);
        let slice = slices.get(id);
        if (!slice) {
            slice = { ancestry: [...ancestry], flowState: new Map() };
            slices.set(id, slice);
        }
        return slice;
    }

    // Tag a cell in a specific slice if not already tagged
    private tagCellInSlice(
        ck: string,
        tag: CellTag,
        slice: SliceState,
        toResolve: Array<{ sliceId: string; cellKey: string }>
    ): void {
        if (!slice.flowState.has(ck)) {
            slice.flowState.set(ck, tag);
            toResolve.push({ sliceId: ancestryToSliceId(slice.ancestry), cellKey: ck });
        }
    }

    // Resolve a drop in the vortex flow algorithm
    private resolveVortexDrop(
        ck: string,
        slice: SliceState,
        slices: Map<string, SliceState>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: Array<{ sliceId: string; cellKey: string }>,
        dropParents: Set<string>
    ): void {
        const tag = slice.flowState.get(ck);
        if (!tag || tag.type !== 'drop' || tag.resolved) return;

        // Record this drop's parent eagerly — the drop may be retagged to crash
        if (tag.parent) dropParents.add(tag.parent);

        const [row, col] = ck.split(',').map(Number);
        const sliceId = ancestryToSliceId(slice.ancestry);

        // Check for gate: neither A nor Z must be in ancestry (prevents bounce-back)
        const pairedKey = this.getPassableGate(ck, slice.ancestry);
        if (pairedKey !== null && !slice.ancestry.includes(pairedKey)) {
            // Mark A in A-slice resolved
            tag.resolved = true;
            // Create Z-slice with ancestry N+A
            const zAncestry = [...slice.ancestry, ck];
            const zSlice = this.getOrCreateSlice(slices, zAncestry);
            // Tag Z as drop in Z-slice with parent (A-slice, A)
            const dropTag: CellTag = { type: 'drop', parent: encodeParent(sliceId, ck), rate: tag.rate, resolved: false };
            this.tagCellInSlice(pairedKey, dropTag, zSlice, toResolve);
            return;
        }

        // If filled or partially filled, retag as crash
        if (filled.has(ck) || waterLevel.has(ck)) {
            const crashTag: CellTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            slice.flowState.set(ck, crashTag);
            this.resolveVortexCrash(ck, slice, slices, filled, waterLevel, toResolve);
            return;
        }

        // Check cell below
        const belowKey = positionKey({ row: row + 1, col });

        // If wall below or at bottom
        if (!this.isBelowAccessible(row, col)) {
            const crashTag: CellTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            slice.flowState.set(ck, crashTag);
            this.resolveVortexCrash(ck, slice, slices, filled, waterLevel, toResolve);
            return;
        }

        // If cell below is filled
        if (filled.has(belowKey)) {
            const crashTag: CellTag = { type: 'crash', parent: tag.parent, rate: tag.rate!, resolved: false };
            slice.flowState.set(ck, crashTag);
            this.resolveVortexCrash(ck, slice, slices, filled, waterLevel, toResolve);
            return;
        }

        // Otherwise, tag cell below as drop and continue
        tag.resolved = true;
        const dropTag: CellTag = { type: 'drop', parent: encodeParent(sliceId, ck), rate: tag.rate, resolved: false };
        this.tagCellInSlice(belowKey, dropTag, slice, toResolve);
    }

    // Resolve a crash in the vortex flow algorithm
    private resolveVortexCrash(
        ck: string,
        slice: SliceState,
        slices: Map<string, SliceState>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: Array<{ sliceId: string; cellKey: string }>
    ): void {
        const tag = slice.flowState.get(ck);
        if (!tag || tag.type !== 'crash' || tag.resolved) return;

        const [row, col] = ck.split(',').map(Number);
        const sliceId = ancestryToSliceId(slice.ancestry);
        tag.resolved = true;

        // If filled or partially filled, tag all connected non-empty cells as spread
        if (filled.has(ck) || waterLevel.has(ck)) {
            const neighbors = this.getConnectedNeighbors(row, col);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagCellInSlice(nk, { type: 'spread', parent: encodeParent(sliceId, ck), via: ck, resolved: false }, slice, toResolve);
                }
            }
        } else if (!this.isBelowAccessible(row, col)) {
            // Wall below: tag horizontal neighbors as spread
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCellInSlice(nk, { type: 'spread', parent: encodeParent(sliceId, ck), via: ck, resolved: false }, slice, toResolve);
                }
            }
        } else {
            // Cell below is filled - tag both B and horizontal neighbors
            const belowKey = positionKey({ row: row + 1, col });
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCellInSlice(nk, { type: 'spread', parent: encodeParent(sliceId, ck), via: ck, resolved: false }, slice, toResolve);
                }
            }
            this.tagCellInSlice(belowKey, { type: 'spread', parent: encodeParent(sliceId, ck), via: ck, resolved: false }, slice, toResolve);
        }

        // Gate check (applies to all crash cases). No `via`: water arriving out of a
        // gate came from somewhere else on the board entirely, so there is no side
        // for a spill here to have arrived on.
        const pairedKey = this.getPassableGate(ck, slice.ancestry);
        if (pairedKey !== null) {
            const zAncestry = [...slice.ancestry, ck];
            const zSlice = this.getOrCreateSlice(slices, zAncestry);
            this.tagCellInSlice(pairedKey, { type: 'spread', parent: encodeParent(sliceId, ck), resolved: false }, zSlice, toResolve);
        }
    }

    // Resolve a spread in the vortex flow algorithm
    private resolveVortexSpread(
        ck: string,
        slice: SliceState,
        slices: Map<string, SliceState>,
        filled: Set<string>,
        waterLevel: Map<string, Rational>,
        toResolve: Array<{ sliceId: string; cellKey: string }>,
        waitingDrops: Array<{ sliceId: string; cellKey: string }>
    ): void {
        const tag = slice.flowState.get(ck);
        if (!tag || tag.type !== 'spread' || tag.resolved) return;

        const [row, col] = ck.split(',').map(Number);
        const parentCrash = tag.parent;
        const sliceId = ancestryToSliceId(slice.ancestry);
        tag.resolved = true;

        // If filled, tag all connected cells as spread
        if (filled.has(ck)) {
            const neighbors = this.getConnectedNeighbors(row, col);
            for (const nk of neighbors) {
                this.tagCellInSlice(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, slice, toResolve);
            }
            // Also check for passable gate
            const pairedKey = this.getPassableGate(ck, slice.ancestry);
            if (pairedKey !== null) {
                const zAncestry = [...slice.ancestry, ck];
                const zSlice = this.getOrCreateSlice(slices, zAncestry);
                // Decode parentCrash to get the original parent info, re-encode with A-slice
                this.tagCellInSlice(pairedKey, { type: 'spread', parent: parentCrash !== null ? encodeParent(sliceId, decodeParent(parentCrash).cellKey) : null, resolved: false }, zSlice, toResolve);
            }
            return;
        }

        // If partially filled, tag connected non-empty cells as spread
        if (waterLevel.has(ck)) {
            const neighbors = this.getConnectedNeighbors(row, col);
            for (const nk of neighbors) {
                if (waterLevel.has(nk) || filled.has(nk)) {
                    this.tagCellInSlice(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, slice, toResolve);
                }
            }
            // Also check for passable gate
            const pairedKey = this.getPassableGate(ck, slice.ancestry);
            if (pairedKey !== null) {
                const zAncestry = [...slice.ancestry, ck];
                const zSlice = this.getOrCreateSlice(slices, zAncestry);
                this.tagCellInSlice(pairedKey, { type: 'spread', parent: parentCrash !== null ? encodeParent(sliceId, decodeParent(parentCrash).cellKey) : null, resolved: false }, zSlice, toResolve);
            }
            return;
        }

        // Cell is empty
        // Check for passable gate — retag as drop (waiting list)
        const pairedKey = this.getPassableGate(ck, slice.ancestry);
        if (pairedKey !== null) {
            const dropTag: CellTag = { type: 'drop', parent: parentCrash, rate: null, resolved: false };
            slice.flowState.set(ck, dropTag);
            waitingDrops.push({ sliceId, cellKey: ck });
            return;
        }

        const belowKey = positionKey({ row: row + 1, col });

        // If wall below, tag horizontal neighbors only
        if (!this.isBelowAccessible(row, col)) {
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCellInSlice(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, slice, toResolve);
                }
            }
            return;
        }

        // If cell below is filled, tag both B and horizontal neighbors
        if (filled.has(belowKey)) {
            const neighbors = this.getHorizontalNeighbors(row, col);
            for (const nk of neighbors) {
                if (!filled.has(nk)) {
                    this.tagCellInSlice(nk, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, slice, toResolve);
                }
            }
            this.tagCellInSlice(belowKey, { type: 'spread', parent: parentCrash, via: ck, resolved: false }, slice, toResolve);
            return;
        }

        // Cell below is empty - this becomes a drop (escape). The side is the step
        // the water took to get here, not the crash it ultimately came from — and
        // `via` is a plain cell key in this slice, so it needs no decoding.
        this.recordDropHint(row, col, tag.via ?? null);
        const dropTag: CellTag = { type: 'drop', parent: parentCrash, rate: null, resolved: false };
        slice.flowState.set(ck, dropTag);
        waitingDrops.push({ sliceId, cellKey: ck });
    }

    // Build flow state with multiple slices for vortex
    private buildVortexFlowState(
        faucetCol: number,
        filled: Set<string>,
        waterLevel: Map<string, Rational>
    ): { slices: Map<string, SliceState>; basins: Basin[] } {
        const slices = new Map<string, SliceState>();

        // Create initial slice with empty ancestry
        const initialSlice: SliceState = { ancestry: [], flowState: new Map() };
        slices.set('', initialSlice);

        const toResolve: Array<{ sliceId: string; cellKey: string }> = [];
        const waitingDrops: Array<{ sliceId: string; cellKey: string }> = [];
        // Track drop parents eagerly — drops may be retagged to crash, losing the link
        const dropParents = new Set<string>();

        // Start with cell below faucet as drop with rate 1
        const startCell = positionKey({ row: 0, col: faucetCol });
        const startTag: CellTag = { type: 'drop', parent: null, rate: { num: 1, denom: 1 }, resolved: false };
        initialSlice.flowState.set(startCell, startTag);
        toResolve.push({ sliceId: '', cellKey: startCell });

        // Resolve all cells
        while (toResolve.length > 0) {
            const { sliceId, cellKey } = toResolve.shift()!;
            const slice = slices.get(sliceId);
            if (!slice) continue;

            const tag = slice.flowState.get(cellKey);
            if (!tag || tag.resolved) continue;

            if (tag.type === 'drop') {
                this.resolveVortexDrop(cellKey, slice, slices, filled, waterLevel, toResolve, dropParents);
            } else if (tag.type === 'crash') {
                this.resolveVortexCrash(cellKey, slice, slices, filled, waterLevel, toResolve);
            } else if (tag.type === 'spread') {
                this.resolveVortexSpread(cellKey, slice, slices, filled, waterLevel, toResolve, waitingDrops);
            }
        }

        // Process waiting drops iteratively until none remain
        while (waitingDrops.length > 0) {
            // Group by parent (encoded as sliceId:cellKey)
            const byParent = new Map<string, Array<{ sliceId: string; cellKey: string }>>();
            const currentWaiting = [...waitingDrops];
            waitingDrops.length = 0;

            for (const entry of currentWaiting) {
                const slice = slices.get(entry.sliceId);
                if (!slice) continue;
                const tag = slice.flowState.get(entry.cellKey) as CellTag & { type: 'drop' };
                const parent = tag.parent!;
                if (!byParent.has(parent)) byParent.set(parent, []);
                byParent.get(parent)!.push(entry);
            }

            // Assign rates and resolve
            for (const [parentEncoded, children] of byParent) {
                const { sliceId: parentSliceId, cellKey: parentCellKey } = decodeParent(parentEncoded);
                const parentSlice = slices.get(parentSliceId);
                if (!parentSlice) continue;

                const parentTag = parentSlice.flowState.get(parentCellKey);
                if (!parentTag || parentTag.type !== 'crash') continue;

                const parentRate = parentTag.rate;
                const childRate = divideRational(parentRate, children.length);

                for (const child of children) {
                    const childSlice = slices.get(child.sliceId);
                    if (!childSlice) continue;
                    const tag = childSlice.flowState.get(child.cellKey) as CellTag & { type: 'drop' };
                    tag.rate = childRate;
                    toResolve.push(child);
                }
            }

            // Resolve the waiting drops (may add more to waitingDrops)
            while (toResolve.length > 0) {
                const { sliceId, cellKey } = toResolve.shift()!;
                const slice = slices.get(sliceId);
                if (!slice) continue;

                const tag = slice.flowState.get(cellKey);
                if (!tag || tag.resolved) continue;

                if (tag.type === 'drop') {
                    this.resolveVortexDrop(cellKey, slice, slices, filled, waterLevel, toResolve, dropParents);
                } else if (tag.type === 'crash') {
                    this.resolveVortexCrash(cellKey, slice, slices, filled, waterLevel, toResolve);
                } else if (tag.type === 'spread') {
                    this.resolveVortexSpread(cellKey, slice, slices, filled, waterLevel, toResolve, waitingDrops);
                }
            }
        }

        // Find terminal crashes across all slices and build basins
        // dropParents was collected eagerly during resolution (drops may be retagged to crash)

        // Collect terminal crashes and their spread children, aggregating by cell position
        // A basin is a group of cells from a single terminal crash
        // But a cell's actual rate is the sum across all slices
        const rawBasins: Array<{ crashParent: string; cells: Set<string>; rate: Rational }> = [];

        for (const [sliceId, slice] of slices) {
            for (const [ck, tag] of slice.flowState) {
                if (tag.type === 'crash') {
                    const crashKey = encodeParent(sliceId, ck);
                    if (!dropParents.has(crashKey)) {
                        // Terminal crash - collect basin cells across all slices
                        const basinCells = new Set<string>();
                        if (!filled.has(ck)) basinCells.add(ck);
                        for (const [, anySlice] of slices) {
                            for (const [sk, stag] of anySlice.flowState) {
                                if (stag.type === 'spread' && stag.parent === crashKey && !filled.has(sk)) {
                                    basinCells.add(sk);
                                }
                            }
                        }
                        rawBasins.push({ crashParent: crashKey, cells: basinCells, rate: tag.rate });
                    }
                }
            }
        }

        // Build the final basins: each raw basin is its own basin
        // The simulation will aggregate rates per cell when computing fill times
        const basins: Basin[] = rawBasins.map(rb => ({
            cells: [...rb.cells],
            rate: rb.rate
        }));

        return { slices, basins };
    }

    protected override simulate(): void {
        // If no active gates, delegate to base class
        if (!this.hasActiveGates()) {
            super.simulate();
            return;
        }

        this.cellFillTime.clear();
        this.dropHints.clear();
        this.debug.clear();

        // Reachability first, so a walled-off region is still reported and shaded on a
        // board whose walls are also disconnected — see FloodGame.simulate.
        this.updateReachability();

        if (!this.validateWallConnectivity()) return;
        if (this.hasUnreachableCells) return;

        const filled = new Set<string>();
        const waterLevel = new Map<string, Rational>();
        let currentTime: Rational = { num: 0, denom: 1 };

        const maxIterations = this.grid.size * this.grid.size * 2;
        let iterations = 0;
        let debugTimestampIndex = 0;

        while (iterations++ < maxIterations && rationalToNumber(currentTime) < 100) {
            const { slices, basins } = this.buildVortexFlowState(this.faucetCol, filled, waterLevel);

            // Capture vortex snapshots at debug timestamps
            debugTimestampIndex = this.debug.maybeCaptureVortexSnapshot(
                rationalToNumber(currentTime),
                debugTimestampIndex,
                filled,
                waterLevel,
                slices,
                basins
            );

            if (basins.length === 0) {
                break;
            }

            // Aggregate fill rates per cell across basins
            // Each basin contributes its rate / basinSize to each of its cells
            // But a cell can appear in multiple basins (from different slices)
            const cellRateContributions = new Map<string, Rational>();
            const cellToBasinIndices = new Map<string, number[]>();

            for (let i = 0; i < basins.length; i++) {
                const basin = basins[i];
                const numCells = basin.cells.length;
                const perCellRate = divideRational(basin.rate, numCells);

                for (const ck of basin.cells) {
                    const existing = cellRateContributions.get(ck);
                    if (existing) {
                        cellRateContributions.set(ck, addRationals(existing, perCellRate));
                    } else {
                        cellRateContributions.set(ck, perCellRate);
                    }
                    if (!cellToBasinIndices.has(ck)) cellToBasinIndices.set(ck, []);
                    cellToBasinIndices.get(ck)!.push(i);
                }
            }

            // Calculate fill time for each unique cell
            let earliestTime: Rational | null = null;

            const cellFillTimes: Array<{ cellKey: string; fillTime: Rational; aggregatedRate: Rational }> = [];

            for (const [ck, rate] of cellRateContributions) {
                const currentLevel = waterLevel.get(ck) ?? { num: 0, denom: 1 };
                const remaining = subtractRationals({ num: 1, denom: 1 }, currentLevel);

                if (compareRationals(remaining, { num: 0, denom: 1 }) <= 0) continue;

                const timeToFill: Rational = reduceRational({
                    num: remaining.num * rate.denom,
                    denom: remaining.denom * rate.num
                });
                const fillTime = addRationals(currentTime, timeToFill);

                cellFillTimes.push({ cellKey: ck, fillTime, aggregatedRate: rate });

                if (earliestTime === null || compareRationals(fillTime, earliestTime) < 0) {
                    earliestTime = fillTime;
                }
            }

            if (earliestTime === null) break;

            // Advance time and update water levels for all cells
            const elapsed = subtractRationals(earliestTime, currentTime);
            for (const { cellKey, aggregatedRate } of cellFillTimes) {
                const added = multiplyRationals(elapsed, aggregatedRate);
                const current = waterLevel.get(cellKey) ?? { num: 0, denom: 1 };
                waterLevel.set(cellKey, addRationals(current, added));
            }

            // Mark cells that complete at the earliest time
            const fillTimeNum = Math.round(rationalToNumber(earliestTime) * 10) / 10;
            for (const { cellKey, fillTime } of cellFillTimes) {
                if (compareRationals(fillTime, earliestTime!) === 0) {
                    this.cellFillTime.set(cellKey, fillTimeNum);
                    filled.add(cellKey);
                }
            }

            currentTime = earliestTime;
        }

        // Check if flow algorithm filled all cells
        if (this.cellFillTime.size < this.grid.size * this.grid.size) {
            return;
        }
    }

    /** Reachability has to follow gates here, not just open edges. Mirrors
     *  {@link simulate}'s delegation: with no active gates there's nothing extra to
     *  follow, and the base constructor reaches this before the gate state exists. */
    protected override reachableFrom(faucetCol: number): Set<string> {
        if (!this.hasActiveGates()) return super.reachableFrom(faucetCol);
        return this.vortexFloodFillReachable(faucetCol);
    }

    // Flood-fill reachability that includes gate teleportation
    private vortexFloodFillReachable(faucetCol: number): Set<string> {
        const reachable = new Set<string>();
        const queue: string[] = [positionKey({ row: 0, col: faucetCol })];
        const directions: Direction[] = ['N', 'S', 'E', 'W'];

        while (queue.length > 0) {
            const key = queue.shift()!;
            if (reachable.has(key)) continue;
            reachable.add(key);

            const pos = parsePositionKey(key);

            // Normal neighbors
            for (const dir of directions) {
                if (!this.hasWallToward(pos, dir)) {
                    const neighbor = this.grid.move(pos, dir);
                    if (isInside(neighbor)) {
                        const nk = positionKey(neighbor);
                        if (!reachable.has(nk)) queue.push(nk);
                    }
                }
            }

            // Gate teleportation
            const pairedKey = this.gm.raw.get(key);
            if (pairedKey && !reachable.has(pairedKey)) {
                queue.push(pairedKey);
            }
        }

        return reachable;
    }

    // ── Debug ──────────────────────────────────────────────────────

    logVortexDebugSnapshots(): void {
        this.debug.logVortexSnapshots();
    }

    logVortexDropTree(): void {
        this.debug.logVortexDropTree();
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.recalculate();
    }
}
